import React, { useState } from 'react'
import LoadingSpinner from '../UI/LoadingSpinner'
import { DEFAULT_STAGES } from '../../utils/scheduleUtils'

/**
 * Stage names and their order.
 *
 * Order is the point: it decides which stage is the top row of the grid, and
 * people expect the main stage first. Names are free-form because every venue
 * names its stages differently — the placeholders below are a starting point
 * to rename, not a taxonomy to conform to.
 */
const StageEditor = ({ stages = [], onSave, saving, onClose }) => {
  const [draft, setDraft] = useState(() => (stages.length > 0 ? [...stages] : [...DEFAULT_STAGES]))
  const [error, setError] = useState('')

  const update = (index, value) => {
    setDraft(current => current.map((stage, i) => (i === index ? value : stage)))
  }

  const move = (index, direction) => {
    const target = index + direction
    if (target < 0 || target >= draft.length) return

    setDraft(current => {
      const next = [...current]
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })
  }

  const remove = (index) => {
    setDraft(current => current.filter((_, i) => i !== index))
  }

  const add = () => setDraft(current => [...current, ''])

  const handleSave = async () => {
    const cleaned = draft.map(stage => stage.trim()).filter(Boolean)

    const duplicate = cleaned.find((stage, index) =>
      cleaned.findIndex(other => other.toLowerCase() === stage.toLowerCase()) !== index
    )
    if (duplicate) {
      // Two rows with the same name would be indistinguishable on the grid and
      // sets would land in whichever came first.
      setError(`"${duplicate}" is listed twice`)
      return
    }

    setError('')
    const ok = await onSave(cleaned)
    if (ok && onClose) onClose()
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-300">
        These become the rows of the schedule, top to bottom. Stages are shared
        with everyone attending this event.
      </p>

      {error && (
        <div className="bg-red-500/20 border border-red-500/40 text-red-200 rounded-lg px-3 py-2 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-2">
        {draft.map((stage, index) => (
          <div key={index} className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-5 text-right tabular-nums">{index + 1}</span>

            <input
              type="text"
              value={stage}
              onChange={(e) => update(index, e.target.value)}
              placeholder={DEFAULT_STAGES[index] || 'Stage name'}
              className="flex-1 px-3 py-2 bg-white/10 border border-white/25 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />

            <div className="flex gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                aria-label={`Move ${stage || 'stage'} up`}
                className="px-2 py-1.5 bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg text-white text-xs"
              >
                ↑
              </button>
              <button
                type="button"
                onClick={() => move(index, 1)}
                disabled={index === draft.length - 1}
                aria-label={`Move ${stage || 'stage'} down`}
                className="px-2 py-1.5 bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg text-white text-xs"
              >
                ↓
              </button>
              <button
                type="button"
                onClick={() => remove(index)}
                aria-label={`Remove ${stage || 'stage'}`}
                className="px-2 py-1.5 bg-red-600/70 hover:bg-red-600 rounded-lg text-white text-xs"
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={add}
        className="text-sm bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg transition-colors"
      >
        + Add stage
      </button>

      <p className="text-xs text-gray-500">
        Removing a stage doesn't delete its sets — they move to an "Unassigned"
        row until you give them a stage.
      </p>

      <div className="flex gap-3 justify-end">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="bg-white/10 hover:bg-white/20 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-2 px-5 rounded-lg transition-all duration-200"
        >
          {saving ? <LoadingSpinner text="Saving…" /> : 'Save stages'}
        </button>
      </div>
    </div>
  )
}

export default StageEditor
